import { Dialog, type DialogProps } from "@cmsgov/design-system-12";
import {
  type ForwardRefRenderFunction,
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ReactNode,
  forwardRef,
} from "react";
import { devDebug } from "../helpers/loggingHelpers";
import { FocusTrap, type FocusTrapProps } from "focus-trap-react";
import classNames from "classnames";
import { TESTING } from "../config";
import { PortalWrapper } from "./PortalWrapper";
import type { FCWithChildren } from "../@types";

export type { DialogProps } from "@cmsgov/design-system-12";

const DialogFocusTrapContainerComponent: ForwardRefRenderFunction<
  HTMLDivElement,
  {
    children: ReactNode;
  }
> = ({ children }, containerRef) => (
  <div className="mct-c-dialog__focus-trap-container" ref={containerRef}>
    {children}
  </div>
);

const DialogFocusTrapContainer = forwardRef(DialogFocusTrapContainerComponent);

/**
 * Simple wrapper around CMSDS Dialog that only renders `children` when open
 */
export const MctDialog: FCWithChildren<
  DialogProps & { hasFocusTrap?: boolean }
> = ({ children, className, isOpen, hasFocusTrap = true, ...props }) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const uniqueId = useId();
  const renderFocusTrapComponent = !TESTING && hasFocusTrap;
  const WrapperEl = renderFocusTrapComponent ? FocusTrap : Fragment;
  const DialogWrapperEl = renderFocusTrapComponent
    ? DialogFocusTrapContainer
    : Fragment;
  const dialogClassName = "mct-c-dialog";
  const focusTrapOptions: FocusTrapProps["focusTrapOptions"] = useMemo(
    () => ({
      checkCanFocusTrap: async trapContainers => {
        const results = trapContainers.map((trapContainer): Promise<void> => {
          let attempt = 0;
          const maxAttempts = 100;
          return new Promise(resolve => {
            let checkFrame: number | undefined;
            function checkCanTrap() {
              if (attempt < maxAttempts) {
                if (getComputedStyle(trapContainer).visibility === "hidden") {
                  attempt++;
                  checkFrame = requestAnimationFrame(checkCanTrap);
                } else {
                  if (checkFrame) {
                    cancelAnimationFrame(checkFrame);
                  }
                  resolve();
                }
              } else {
                if (checkFrame) {
                  cancelAnimationFrame(checkFrame);
                }
              }
            }
            checkCanTrap();
          });
        });
        await Promise.all(results);
        // return when all the trap containers are able to receive focus
        return;
      },
      onActivate: () => {
        devDebug("🪤 Focus trap activated");
        dialogRef.current?.classList.add("focus-trap-active");
      },
      onDeactivate: () => {
        devDebug("🪤 Focus trap being deactivated");
        dialogRef.current?.classList.remove("focus-trap-active");
      },
      // Called after focus is sent to the focus trap
      onPostActivate: () => {
        devDebug("🪤 Focus has been sent to the animated focus trap");
      },
      returnFocusOnDeactivate: true,
    }),
    []
  );

  useEffect(() => {
    if (!dialogRef.current) {
      dialogRef.current = document.querySelector(
        `[data-id="${uniqueId}"]`
      ) as HTMLDialogElement | null;
    }
  }, [uniqueId]);

  return (
    <PortalWrapper renderToPortal={renderFocusTrapComponent}>
      <WrapperEl
        {...(renderFocusTrapComponent
          ? { active: isOpen, focusTrapOptions }
          : {})}
      >
        <DialogWrapperEl>
          <Dialog
            className={classNames(className, dialogClassName)}
            isOpen={isOpen}
            data-id={uniqueId}
            {...props}
          >
            {isOpen && children}
          </Dialog>
        </DialogWrapperEl>
      </WrapperEl>
    </PortalWrapper>
  );
};
